import type {
  MediaController,
  MediaControllerEventHandlers,
  MediaControllerInitializeOptions,
  MediaRecorderEventHandlers,
  MediaRecorderWaveformSamples,
  MediaTranscriberEventHandlers,
} from "@/store/audio/recording/audio-recording.types";
import { DesktopMediaRecorder } from "@/store/audio/recording/media-recorder/desktop-media-recorder";
import { DesktopMediaTranscriber } from "@/store/audio/recording/media-transcriber/desktop-media-transcriber";
import { DesktopCaptureService } from "@/modules/audio-recording/legacy";
import type { DesktopRecorderTranscriptTurn } from "@/store/audio/recording/media-transcriber/transcriber.types";
import { audioRecordingModule } from "@/modules/audio-recording";
import { DESKTOP_PCM_SAMPLE_RATE } from "@/modules/audio-recording/legacy";
import type { AudioLevelProbe } from "@/modules/audio-recording/legacy";
import { DesktopMediaVisualizer } from "@/store/audio/recording/media-visualizer/desktop-media-visualizer";
import { DesktopPcmProbe } from "@/modules/audio-recording/legacy";
import { TranscriptionStatus } from "@/store/audio/recording/media-types";
import { TranscriptionMonitor } from "@/store/audio/recording/media-transcriber/transcription-monitor";
import { PreparedRecording } from "@/store/audio/recording/prepared-recording-controller";
import { TimeoutError } from "@/domains/errors/kinds/TimeoutError";
import { logger } from "@/modules/logger";

export type DesktopMediaControllerOptions = {
  eventHandlers: MediaControllerEventHandlers;
  preparedRecording: PreparedRecording;
};

export class DesktopMediaController implements MediaController {
  private readonly handlers: MediaControllerEventHandlers;
  private readonly captureService: DesktopCaptureService<DesktopRecorderTranscriptTurn>;
  private readonly recorder: DesktopMediaRecorder;
  private readonly transcriber: DesktopMediaTranscriber;
  private readonly visualizerEngine = new DesktopMediaVisualizer();
  private visualizer: AudioLevelProbe | null = null;
  private probe: DesktopPcmProbe | null = null;
  private isInitialized = false;
  private readonly abortController = new AbortController();
  private recorderNotifiedStop = false;
  private readonly transcriptionMonitor = new TranscriptionMonitor({ maxReconnectAttempts: 5 });

  constructor({ eventHandlers, preparedRecording }: DesktopMediaControllerOptions) {
    this.handlers = eventHandlers;

    const recorderRef: { current: DesktopMediaRecorder | null } = { current: null };
    const transcriberRef: { current: DesktopMediaTranscriber | null } = { current: null };

    const emitError = (error: unknown) => {
      this.handlers.onError?.({ error });
    };

    this.captureService = new DesktopCaptureService<DesktopRecorderTranscriptTurn>(
      preparedRecording,
      {
        onTranscript: (channel, turn) => {
          const current = transcriberRef.current;
          if (current) {
            current.handleTranscript({ channel, turn });
          }
        },
        onPcm: samples => {
          const current = recorderRef.current;
          if (current) {
            current.handleSamples(samples);
          }
          this.pushWaveformSamples(samples);
        },
        onDebugEvent: (data, kind) => {
          this.handlers.onDebugEvent?.({ data, kind });
        },
      },
      {
        transcriptMapper: raw =>
          audioRecordingModule.normalizeDesktopTranscriptTurn({
            raw,
            fallbackTimestamp: Date.now(),
          }),
      }
    );

    const recorder = new DesktopMediaRecorder({
      captureService: this.captureService,
      sampleRate: DESKTOP_PCM_SAMPLE_RATE,
      emitError,
    });
    recorderRef.current = recorder;

    const transcriber = new DesktopMediaTranscriber({ emitError });
    transcriberRef.current = transcriber;

    this.recorder = recorder;
    this.transcriber = transcriber;

    const recorderHandlers: MediaRecorderEventHandlers = {
      onAudioChunk: ({ chunk }) => {
        this.handlers.onAudioChunk?.({ chunk });
      },
      onError: ({ error }) => {
        this.handlers.onError?.({ error });
      },
      onStop: () => {
        this.recorderNotifiedStop = true;
        this.handlers.onStop?.();
      },
    };
    this.recorder.setEventHandlers(recorderHandlers);

    const transcriberHandlers: MediaTranscriberEventHandlers = {
      onTranscriptTurn: ({ turn }) => {
        this.emitTranscriptionStatus(this.transcriptionMonitor.markConnected());
        this.handlers.onTranscriptTurn?.({ turn });
      },
      onError: ({ error }) => {
        this.handlers.onError?.({ error });
        this.emitTranscriptionStatus(this.transcriptionMonitor.markDisconnected());
      },
      onStatusChange: ({ status }) => {
        this.handlers.onTranscriptionStatusChange?.({ status });
      },
    };
    this.transcriber.setEventHandlers(transcriberHandlers);
  }

  private readonly INITIALIZATION_TIMEOUT_MS = 7_000;

  async initializeAndStart(options: MediaControllerInitializeOptions): Promise<void> {
    try {
      let initialized = false;
      await Promise.race([
        this._initializeAndStart(options),
        new Promise<never>((_, reject) =>
          setTimeout(() => {
            if (initialized) return;

            this.abortController.abort();
            reject(new TimeoutError({ message: "Initialization timed out" }));

            logger.warn({
              message: "[DesktopMediaController] Initialization timed out",
              info: {
                audioRecordingId: options.audioRecordingId,
                noteId: options.noteId,
              },
            });
          }, this.INITIALIZATION_TIMEOUT_MS)
        ),
      ]);
      initialized = true;
    } catch (error) {
      options.onFallbackToWebMedia?.();
      throw error;
    }
  }

  async _initializeAndStart(options: MediaControllerInitializeOptions): Promise<void> {
    await this.initialize(options);
    options.onBeginRecording?.();
    await this.start();
  }

  async initialize(options: MediaControllerInitializeOptions): Promise<void> {
    if (this.isInitialized) return;

    const noteId = options.noteId;
    const audioRecordingId = options.audioRecordingId;

    if (!audioRecordingId) {
      throw new Error("Desktop recorder requires an associated audio recording.");
    }

    await this.captureService.prepare({
      associatedAudioRecordingId: audioRecordingId,
      associatedNoteId: noteId,
      detectedMeetingWindowId: options.detectedMeetingWindowId,
      abortSignal: this.abortController.signal,
    });
    if (this.abortController.signal.aborted) return;

    await this.ensureVisualizer(DESKTOP_PCM_SAMPLE_RATE);

    this.isInitialized = true;
  }

  async start(): Promise<void> {
    if (this.abortController.signal.aborted) return;
    await this.transcriber.start();
    this.emitTranscriptionStatus(this.transcriptionMonitor.markConnected());

    if (this.abortController.signal.aborted) return;

    await this.recorder.start(this.abortController.signal);

    if (this.abortController.signal.aborted) return;

    this.handlers.onStart?.();
  }

  async stop(): Promise<void> {
    await this.recorder.stop();
    await this.transcriber.stop();
    if (!this.recorderNotifiedStop) {
      this.handlers.onStop?.();
    }
    this.recorderNotifiedStop = false;
    this.isInitialized = false;
    await this.teardownVisualizer();
  }

  async pause(): Promise<void> {
    await this.recorder.pause();
  }

  async resume(): Promise<void> {
    await this.recorder.resume();
  }

  getVisualizer(): AudioLevelProbe | null {
    return this.visualizer;
  }

  dispose(): void {
    this.recorderNotifiedStop = false;
    this.isInitialized = false;
    void this.transcriber.stop();
    void this.recorder.stop();
    this.recorder.dispose();
    this.transcriber.dispose();
    this.captureService.dispose();
    void this.teardownVisualizer();
  }

  private pushWaveformSamples(samples: Int16Array): void {
    if (!samples.length) return;
    this.probe?.pushSamples(samples);

    if (this.handlers.onWaveformSamples) {
      const payload: MediaRecorderWaveformSamples = {
        sampleRate: DESKTOP_PCM_SAMPLE_RATE,
        pcm: samples.slice(),
      };
      this.handlers.onWaveformSamples({ samples: payload });
    }
  }

  private async ensureVisualizer(sampleRate: number): Promise<void> {
    try {
      const { visualizer } = await this.visualizerEngine.start({ sampleRate });
      if (this.abortController.signal.aborted) await this.visualizerEngine.stop();
      this.visualizer = visualizer;
      this.probe = visualizer as DesktopPcmProbe;
    } catch {
      this.visualizer = null;
      this.probe = null;
    }
  }

  private async teardownVisualizer(): Promise<void> {
    this.probe = null;
    this.visualizer = null;
    await this.visualizerEngine.stop();
  }

  private emitTranscriptionStatus(status: TranscriptionStatus | null) {
    if (!status) return;
    this.handlers.onTranscriptionStatusChange?.({ status });
  }
}
